import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BrandSeat,
  inviteUserToCustomer,
  listBrandSeats,
  removeBrandSeat,
  SeatRole,
} from "../../../services/customerService";
import { useAuth } from "../../../context/AuthContext";
import { isSuperAdmin } from "../../../utils/permission";
import ConfirmDialog from "../../../components/Shared/ConfirmDialog";
import { hasSeatRoom, seatsRemaining, UsageSummary } from "../../../services/usageService";

/**
 * Team card on the Billing page (SGL-398). Shows who holds Seats on the Brand,
 * how many the Owner's plan includes, and lets the Owner invite a teammate on
 * this Brand or — on Multi Brand / Agency Pro — an agency operator, who works
 * across every client Brand and counts against the plan's operator seats.
 *
 * Colours come from the app's theme tokens so the card matches every other
 * screen in light and dark mode. The only additions are the operator gold and
 * the over-plan amber, which have no token; each carries a dark variant.
 *
 * SuperAdmin (SGL-555) manages any Brand from here whatever their own Seat:
 * they always see Invite, can choose Owner or Member, and can remove people.
 * The server already allows both for SuperAdmin and skips the seat cap.
 */

const PLAN_LABELS: Record<string, string> = {
  "sagelyn starter": "Sagelyn Starter",
  "sagelyn pro": "Sagelyn Pro",
  "sagelyn multi brand": "Sagelyn Multi Brand",
  "sagelyn agency pro": "Sagelyn Agency Pro",
};

const GOLD_TEXT = "text-[#6E5520] dark:text-[#E2C58F]";
const GOLD_SOFT = "bg-[#F6EEDD] dark:bg-[#4A3F2C]";
// Tailwind's `/NN` opacity shorthand does nothing on the theme colours: they are
// plain CSS variables, not `<alpha-value>` channels. So tints are explicit
// colours, each with a dark-mode partner, matched to the approved design.
const SAGE_SOFT = "bg-[#E7EFEA] dark:bg-[#3A4A44]";

const SEAT_ROLES: { value: SeatRole; label: string; detail: string }[] = [
  { value: "MEMBER", label: "Member", detail: "Creates, approves and publishes. No billing, seats or connections." },
  { value: "OWNER", label: "Owner", detail: "Everything, plus billing, people, connections. Use for the paying customer." },
];

type Props = {
  customerId: string;
  businessName: string;
  usage: UsageSummary;
  idToken: string;
  onChanged: () => void | Promise<void>;
};

/** A Seat can outlive its Users row (SGL-357), leaving no name or email. Say so rather than render a blank row. */
function label(seat: BrandSeat): string {
  return seat.display_name || seat.email || "Unknown person";
}

function initials(seat: BrandSeat): string {
  const source = (seat.display_name || seat.email || "?").trim();
  const parts = source.split(/[\s@._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "?") + (parts[1]?.[0] ?? "")).toUpperCase();
}

/** One block per seat when the cap is small enough to read as blocks; a bar otherwise. */
const SeatMeter: React.FC<{ used: number; max: number; fillClass: string; emptyClass: string }> = ({
  used,
  max,
  fillClass,
  emptyClass,
}) => {
  if (max <= 12) {
    return (
      <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${max}, minmax(0, 1fr))` }}>
        {Array.from({ length: max }, (_, i) => (
          <span key={i} className={`h-2 rounded ${i < used ? fillClass : emptyClass}`} />
        ))}
      </div>
    );
  }
  return (
    <div className={`h-2 overflow-hidden rounded ${emptyClass}`}>
      <div className={`h-full rounded ${fillClass}`} style={{ width: `${Math.min(100, (used / max) * 100)}%` }} />
    </div>
  );
};

const TeamSeatsCard: React.FC<Props> = ({ customerId, businessName, usage, idToken, onChanged }) => {
  const [seats, setSeats] = useState<BrandSeat[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [pendingRemove, setPendingRemove] = useState<BrandSeat | null>(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [kind, setKind] = useState<"BRAND" | "AGENCY">("BRAND");
  const [role, setRole] = useState<SeatRole>("MEMBER");
  const { userGroups } = useAuth();
  const viewerIsSuperAdmin = isSuperAdmin(userGroups);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState<{ tone: "ok" | "error"; text: string } | null>(null);

  const loadSeats = useCallback(async () => {
    try {
      setListError(null);
      setSeats(await listBrandSeats(customerId, idToken));
    } catch {
      setListError("Couldn't load the people on this Brand. Refresh to try again.");
    }
  }, [customerId, idToken]);

  useEffect(() => {
    loadSeats();
  }, [loadSeats]);

  const brandUnlimited = usage.seats_unlimited;
  const brandUnknown = !brandUnlimited && (usage.seats_error === true || usage.max_seats === null);
  const brandRoom = usage.seats_error === true || hasSeatRoom(usage.seats_used, usage.max_seats, brandUnlimited);
  const brandLeft = seatsRemaining(usage.seats_used, usage.max_seats, brandUnlimited);
  const hasOperators = usage.operator_seats_unlimited || (usage.operator_seats_max ?? 0) > 0;
  // On an agency plan the meter that matters is the agency's own team, and the
  // Owner is one of its seats: Multi Brand's 3 = the Owner + two more
  // (Sourav, 2026-10-04). The server's operator count already includes the
  // Owner, so showing a separate "This Brand 1 of 1" box beside it would count
  // the Owner twice. Client logins sit on client Brands, outside the team.
  const teamUsed = hasOperators ? usage.operator_seats_used : usage.seats_used;
  const teamMax = hasOperators ? usage.operator_seats_max : usage.max_seats;
  const teamUnlimited = hasOperators ? usage.operator_seats_unlimited : brandUnlimited;
  const overCap = !teamUnlimited && teamMax !== null && teamUsed > teamMax;
  const opRoom = hasOperators && hasSeatRoom(usage.operator_seats_used, usage.operator_seats_max, usage.operator_seats_unlimited);
  const opLeft = seatsRemaining(usage.operator_seats_used, usage.operator_seats_max, usage.operator_seats_unlimited);

  const viewerIsOwner = !!seats?.some((s) => s.is_you && s.kind === "owner");
  const canInvite = brandRoom || opRoom;
  const canManage = viewerIsOwner || viewerIsSuperAdmin;
  const showInvite = viewerIsSuperAdmin || (viewerIsOwner && canInvite);

  // Default the choice to whichever kind still has room.
  useEffect(() => {
    if (kind === "BRAND" && !brandRoom && opRoom) setKind("AGENCY");
    if (kind === "AGENCY" && !opRoom && brandRoom) setKind("BRAND");
  }, [kind, brandRoom, opRoom]);

  const planLabel = usage.plan ? PLAN_LABELS[usage.plan] ?? usage.plan : null;

  const sortedSeats = useMemo(() => seats ?? [], [seats]);

  const handleRemove = async (seat: BrandSeat) => {
    const who = label(seat);
    setRemoving(seat.user_id);
    setMessage(null);
    try {
      await removeBrandSeat(customerId, seat.user_id, idToken);
      setMessage({ tone: "ok", text: `${who} no longer has access to ${businessName}.` });
      await Promise.all([loadSeats(), onChanged()]);
    } catch (e) {
      setMessage({ tone: "error", text: e instanceof Error ? e.message : "Couldn't remove them." });
    } finally {
      setRemoving(null);
      setPendingRemove(null);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setMessage(null);
    try {
      // An Owner seat is the Brand's own, never an Agency operator's.
      const allowance = role === "OWNER" ? "BRAND" : kind;
      const res = (await inviteUserToCustomer(
        customerId,
        { email, name, role, allowance },
        idToken
      )) as { already_existed_in_cognito?: boolean; role?: SeatRole; assignment?: { role?: SeatRole } };
      const as =
        (res?.role ?? res?.assignment?.role) === "OWNER"
          ? `the Owner of ${businessName || "this Brand"}`
          : allowance === "AGENCY" ? "a member of your team" : hasOperators ? "this Brand's client login" : "a teammate";
      setMessage({
        tone: "ok",
        text: res?.already_existed_in_cognito
          ? `${email.trim()} already had a login — they've been added as ${as}.`
          : `Invite sent to ${email.trim()}. They've been added as ${as}.`,
      });
      setEmail("");
      setName("");
      setRole("MEMBER");
      await Promise.all([loadSeats(), onChanged()]);
    } catch (err) {
      setMessage({ tone: "error", text: err instanceof Error ? err.message : "Couldn't send the invite." });
    } finally {
      setSending(false);
    }
  };

  const inputClass =
    "h-11 w-full rounded-lg border border-theme-border bg-theme-surface px-3.5 text-[15px] text-theme-fg placeholder:text-[#8A958F] focus:border-theme-primary focus:outline-none focus:ring-2 focus:ring-[#2f4941]/20";

  return (
    <section className="overflow-hidden rounded-2xl border border-theme-border bg-theme-surface shadow-sm">
      {/* Header */}
      <header className="flex flex-wrap items-start justify-between gap-4 px-6 pb-5 pt-6 sm:flex-nowrap sm:px-8">
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <h2 className="text-xl font-extrabold tracking-tight text-theme-fg">Team</h2>
          <p className="text-sm leading-relaxed text-theme-fg-muted">
            People who can work on <strong className="font-bold text-theme-fg">{businessName || "this Brand"}</strong> and how many your plan includes.
          </p>
        </div>
        {planLabel && (
          <span className={`shrink-0 rounded-full ${SAGE_SOFT} px-3 py-1.5 text-[13px] font-bold text-theme-primary dark:text-theme-fg`}>
            {planLabel}
          </span>
        )}
      </header>

      {/* Over the plan */}
      {overCap && (
        <div className="px-6 pb-6 sm:px-8">
          <div role="status" className="flex gap-3.5 rounded-xl border border-[#F0DDB8] bg-[#FDF4E3] p-4 dark:border-[#6B5326] dark:bg-[#3A3121]">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="mt-px shrink-0 text-[#8A5A00] dark:text-[#E8C27A]">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7.5v5.5" />
              <path d="M12 16.5h.01" />
            </svg>
            <div className="flex flex-col gap-1">
              <span className="text-[15px] font-extrabold text-[#5C3D00] dark:text-[#F3D9A6]">
                {teamUsed} people — your plan includes {teamMax}
              </span>
              <span className="text-sm leading-relaxed text-[#6B4A08] dark:text-[#E8CFA0]">
                Everyone keeps access for now. To invite anyone new, remove someone or change your plan below.
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Meter */}
      {!overCap && (
        <div className="px-6 pb-7 sm:px-8">
          <div className="flex flex-col gap-3 rounded-xl border border-theme-border p-[18px]">
            <div className="flex items-baseline justify-between">
              <span className="text-[13px] font-bold uppercase tracking-wider text-theme-fg-muted">
                {hasOperators ? "Your team" : "This Brand"}
              </span>
              <span className="text-[13px] font-semibold text-theme-fg-muted">
                {teamUnlimited ? "Unlimited" : brandUnknown && !hasOperators ? "" : seatsRemaining(teamUsed, teamMax, teamUnlimited) === 0 ? "Full" : `${seatsRemaining(teamUsed, teamMax, teamUnlimited)} left`}
              </span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-3xl font-extrabold tracking-tight text-theme-fg">{teamUsed}</span>
              <span className="text-[15px] text-theme-fg-muted">
                {teamUnlimited || teamMax === null
                  ? teamUsed === 1 ? "seat used" : "seats used"
                  : `of ${teamMax} ${teamMax === 1 ? "seat" : "seats"} used`}
              </span>
            </div>
            {!teamUnlimited && teamMax !== null && teamMax > 0 && (
              <SeatMeter used={teamUsed} max={teamMax} fillClass="bg-theme-primary" emptyClass="bg-[#DCE5E0] dark:bg-[#4B5A54]" />
            )}
            {hasOperators && (
              <p className="text-[13px] leading-relaxed text-theme-fg-muted">
                Includes you. Your team works across every client Brand; each client Brand also gets one login of its own for that client.
              </p>
            )}
          </div>
        </div>
      )}

      {/* People */}
      <ul className="border-t border-theme-border">
        {listError && <li className="px-6 py-4 text-sm text-theme-danger sm:px-8">{listError}</li>}
        {!seats && !listError && <li className="px-6 py-4 text-sm text-theme-fg-muted sm:px-8">Loading people…</li>}
        {sortedSeats.map((seat) => {
          const gold = seat.kind === "operator";
          return (
            <li key={seat.user_id} className="flex items-center gap-3.5 border-b border-theme-border px-6 py-4 last:border-b-0 sm:px-8">
              <span
                aria-hidden="true"
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                  seat.kind === "owner" ? "bg-theme-primary text-white" : gold ? `${GOLD_SOFT} ${GOLD_TEXT}` : `${SAGE_SOFT} text-theme-primary dark:text-theme-fg`
                }`}
              >
                {initials(seat)}
              </span>
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate text-[15px] font-bold text-theme-fg">
                  {label(seat)}
                  {seat.is_you && <span className="font-medium text-theme-fg-muted"> (you)</span>}
                </span>
                {seat.display_name && seat.email && <span className="truncate text-[13px] text-theme-fg-muted">{seat.email}</span>}
                {!seat.display_name && !seat.email && (
                  <span className="truncate text-[13px] text-theme-fg-muted">No account details found for this seat</span>
                )}
              </div>
              <span
                className={`shrink-0 rounded-md px-2.5 py-1 text-xs font-bold ${
                  seat.kind === "owner" ? `${SAGE_SOFT} text-theme-primary dark:text-theme-fg` : gold ? `${GOLD_SOFT} ${GOLD_TEXT}` : "bg-[#EEF1EE] text-theme-fg dark:bg-[#555453]"
                }`}
              >
                {seat.kind === "owner" ? "Owner" : gold ? "Team" : hasOperators ? "Client" : "Member"}
              </span>
              {canManage && (
                <span className="w-[76px] shrink-0 text-right">
                  {seat.kind !== "owner" && (
                    <button
                      type="button"
                      onClick={() => setPendingRemove(seat)}
                      disabled={removing === seat.user_id}
                      aria-label={`Remove ${label(seat)}`}
                      className="h-9 rounded-lg border border-theme-border bg-theme-surface px-3 text-[13px] font-semibold text-theme-fg transition-colors hover:border-theme-danger hover:text-theme-danger disabled:opacity-50"
                    >
                      {removing === seat.user_id ? "…" : "Remove"}
                    </button>
                  )}
                </span>
              )}
            </li>
          );
        })}
      </ul>

      {message && (
        <p role="status" className={`border-t border-theme-border px-6 py-3 text-sm sm:px-8 ${message.tone === "ok" ? "text-theme-primary dark:text-theme-fg" : "text-theme-danger"}`}>
          {message.text}
        </p>
      )}

      {/* Invite — Owner or SuperAdmin; the server enforces it too. */}
      {showInvite && (
        <form onSubmit={handleInvite} className="flex flex-col gap-5 border-t border-theme-border bg-theme-background px-6 pb-8 pt-7 sm:px-8">
          <div className="flex flex-col gap-1">
            <h3 className="text-[17px] font-extrabold text-theme-fg">{hasOperators ? "Invite to your team" : "Invite a teammate"}</h3>
            <p className="text-sm leading-relaxed text-theme-fg-muted">
              {hasOperators
                ? "They'll work across all your client Brands, and get an email with a link to set their password."
                : `They get their own login to ${businessName || "this Brand"} — its agents, content and connections — and spend from its credits.`}
            </p>
          </div>

          {viewerIsSuperAdmin && (
            <fieldset className="flex flex-col gap-2.5">
              <legend className="mb-2.5 text-[13px] font-bold text-theme-fg">Their role on this Brand</legend>
              <div className="grid gap-3 sm:grid-cols-2">
                {SEAT_ROLES.map((o) => (
                  <label
                    key={o.value}
                    className={`flex cursor-pointer gap-3 rounded-xl bg-theme-surface p-4 ${
                      role === o.value ? "border-2 border-theme-primary" : "border border-theme-border"
                    }`}
                  >
                    <input type="radio" name="seat-role" value={o.value} checked={role === o.value} onChange={() => setRole(o.value)} className="mt-0.5 text-theme-primary focus:ring-theme-primary" />
                    <span className="flex flex-col gap-1">
                      <span className="text-sm font-bold text-theme-fg">{o.label}</span>
                      <span className="text-[13px] leading-snug text-theme-fg-muted">{o.detail}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          {hasOperators && brandRoom && opRoom && role !== "OWNER" && (
            <fieldset className="flex flex-col gap-2.5">
              <legend className="mb-2.5 text-[13px] font-bold text-theme-fg">What will they do?</legend>
              <div className="grid gap-3 sm:grid-cols-2">
                <label
                  className={`flex gap-3 rounded-xl p-4 ${
                    !brandRoom
                      ? "cursor-not-allowed border border-theme-border bg-theme-background opacity-70"
                      : kind === "BRAND"
                      ? "cursor-pointer border-2 border-theme-primary bg-theme-surface"
                      : "cursor-pointer border border-theme-border bg-theme-surface"
                  }`}
                >
                  <input type="radio" name="seat-kind" value="BRAND" checked={kind === "BRAND"} disabled={!brandRoom} onChange={() => setKind("BRAND")} className="mt-0.5 text-theme-primary focus:ring-theme-primary" />
                  <span className="flex flex-col gap-1">
                    <span className="text-sm font-bold text-theme-fg">{hasOperators ? "Client login for this Brand" : "Teammate on this Brand"}</span>
                    <span className="text-[13px] leading-snug text-theme-fg-muted">
                      {brandRoom ? (brandUnlimited ? "Sees this Brand only." : `Sees this Brand only. ${brandLeft} left.`) : "No seats left on this Brand."}
                    </span>
                  </span>
                </label>
                <label
                  className={`flex gap-3 rounded-xl p-4 ${
                    !opRoom
                      ? "cursor-not-allowed border border-theme-border bg-theme-background opacity-70"
                      : kind === "AGENCY"
                      ? "cursor-pointer border-2 border-[#A9772A] bg-theme-surface dark:border-theme-accent-warm"
                      : "cursor-pointer border border-theme-border bg-theme-surface"
                  }`}
                >
                  <input type="radio" name="seat-kind" value="AGENCY" checked={kind === "AGENCY"} disabled={!opRoom} onChange={() => setKind("AGENCY")} className="mt-0.5 text-[#A9772A] focus:ring-[#A9772A]" />
                  <span className="flex flex-col gap-1">
                    <span className="text-sm font-bold text-theme-fg">Your team</span>
                    <span className="text-[13px] leading-snug text-theme-fg-muted">
                      {opRoom
                        ? usage.operator_seats_unlimited
                          ? "Works on all your client Brands."
                          : `Works on all your client Brands. ${opLeft} of ${usage.operator_seats_max} left.`
                        : "No operator seats left."}
                    </span>
                  </span>
                </label>
              </div>
            </fieldset>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5 text-[13px] font-bold text-theme-fg">
              Email
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" className={inputClass} />
            </label>
            <label className="flex flex-col gap-1.5 text-[13px] font-bold text-theme-fg">
              Name
              <input type="text" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className={inputClass} />
            </label>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4">
            <span className="text-[13px] text-theme-fg-muted">
              {viewerIsSuperAdmin ? "Sagelyn admin: seat limits don't apply to you." : "Need more seats? Change your plan below."}
            </span>
            <button
              type="submit"
              disabled={sending || !email.trim() || !name.trim()}
              className="h-11 rounded-lg bg-theme-primary px-5 text-[15px] font-bold text-white transition-colors hover:bg-theme-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sending ? "Sending…" : "Send invite"}
            </button>
          </div>
        </form>
      )}

      {viewerIsOwner && !viewerIsSuperAdmin && !canInvite && !overCap && (
        <p className="border-t border-theme-border bg-theme-background px-6 py-5 text-sm text-theme-fg-muted sm:px-8">
          Every seat on your plan is in use. Remove someone or change your plan below to invite more people.
        </p>
      )}
      <ConfirmDialog
        isOpen={pendingRemove !== null}
        title={pendingRemove ? `Remove ${label(pendingRemove)}?` : "Remove this person?"}
        message={
          pendingRemove
            ? `Remove ${label(pendingRemove)} from ${businessName}? They keep their login but lose access to this Brand.`
            : ""
        }
        confirmLabel="Remove"
        destructive
        isConfirming={removing !== null}
        onConfirm={() => {
          if (pendingRemove) void handleRemove(pendingRemove);
        }}
        onCancel={() => setPendingRemove(null)}
      />
    </section>
  );
};

export default TeamSeatsCard;
